import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ChevronRight, Phone, Users, ListChecks, Trophy, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getServiceBySlug, getServices, createLead } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ImageSlider } from '../components/ImageSlider';
import { useLanguage } from '../lib/LanguageContext';

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [relatedServices, setRelatedServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [serviceRes, servicesRes] = await Promise.all([
          getServiceBySlug(slug),
          getServices()
        ]);
        setService(serviceRes.data);
        setRelatedServices(
          servicesRes.data.filter(s => s.slug !== slug).slice(0, 3)
        );
      } catch (err) {
        console.error('Error fetching service:', err);
        toast.error(language === 'en' ? 'Service not found' : 'Leistung nicht gefunden');
        navigate('/leistungen');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate, language]);

  // Helper function to get localized service field
  const getLocalizedField = (serviceObj, field) => {
    const enField = `${field}_en`;
    const deField = `${field}_de`;
    if (language === 'en' && serviceObj[enField]) {
      return serviceObj[enField];
    }
    return serviceObj[deField] || serviceObj[field] || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await createLead({
        ...formData,
        service_id: service?.id,
      });
      toast.success(t('contactPage.successMessage'));
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      console.error('Error submitting form:', err);
      toast.error(t('contactPage.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  return (
    <div data-testid="service-detail-page">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        {service.image_url && (
          <>
            <div className="absolute inset-0">
              <img 
                src={service.image_url} 
                alt={getLocalizedField(service, 'title')}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/80 to-[#0A0A0A]/60" />
          </>
        )}
        <div className={`relative max-w-7xl mx-auto px-6 ${!service.image_url ? 'bg-[#0A0A0A] py-16' : ''}`}>
          <Link 
            to="/leistungen" 
            className="inline-flex items-center gap-2 text-[#D4B982] hover:text-white transition-colors mb-6"
            data-testid="back-to-services"
          >
            <ArrowLeft size={16} />
            {t('serviceDetail.backToServices')}
          </Link>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            {getLocalizedField(service, 'title')}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            {getLocalizedField(service, 'short')}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Description */}
              <div>
                <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#D4B982]/20 rounded-sm flex items-center justify-center">
                    <ListChecks className="text-[#D4B982]" size={20} />
                  </span>
                  {t('serviceDetail.description')}
                </h2>
                <p className="text-[#71717A] leading-relaxed">{getLocalizedField(service, 'long')}</p>
              </div>

              {/* Features */}
              {service.features && service.features.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4B982]/20 rounded-sm flex items-center justify-center">
                      <CheckCircle2 className="text-[#D4B982]" size={20} />
                    </span>
                    {t('serviceDetail.whatWeDo')}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.features.map((feature, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-3 p-4 bg-[#F4F4F5] rounded-sm"
                      >
                        <CheckCircle2 className="text-[#D4B982] flex-shrink-0" size={18} />
                        <span className="text-[#0A0A0A]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Target Audience */}
              {service.target_audience && (
                <div>
                  <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4B982]/20 rounded-sm flex items-center justify-center">
                      <Users className="text-[#D4B982]" size={20} />
                    </span>
                    {t('serviceDetail.forWhom')}
                  </h2>
                  <p className="text-[#71717A] leading-relaxed">{service.target_audience}</p>
                </div>
              )}

              {/* Process */}
              {service.process && (
                <div>
                  <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4B982]/20 rounded-sm flex items-center justify-center">
                      <ListChecks className="text-[#D4B982]" size={20} />
                    </span>
                    {t('serviceDetail.process')}
                  </h2>
                  <div className="bg-[#F4F4F5] p-6 rounded-sm">
                    <p className="text-[#0A0A0A]">{service.process}</p>
                  </div>
                </div>
              )}

              {/* Benefits */}
              {service.benefits && service.benefits.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4B982]/20 rounded-sm flex items-center justify-center">
                      <Trophy className="text-[#D4B982]" size={20} />
                    </span>
                    {t('serviceDetail.benefits')}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.benefits.map((benefit, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-3 p-4 border border-[#D4B982]/30 rounded-sm"
                      >
                        <div className="w-8 h-8 bg-[#D4B982] rounded-sm flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <span className="text-[#0A0A0A]">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Image Gallery */}
              {((service.gallery && service.gallery.length > 0) || service.image_url) && (
                <div>
                  <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#D4B982]/20 rounded-sm flex items-center justify-center">
                      <ImageIcon className="text-[#D4B982]" size={20} />
                    </span>
                    {t('serviceDetail.gallery')}
                  </h2>
                  <ImageSlider 
                    images={service.gallery && service.gallery.length > 0 
                      ? service.gallery 
                      : [service.image_url]
                    } 
                    title={getLocalizedField(service, 'title')}
                  />
                </div>
              )}
            </div>

            {/* Sidebar - Contact Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-[#F4F4F5] p-8 rounded-sm">
                <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">{t('serviceDetail.requestQuote')}</h3>
                <p className="text-[#71717A] text-sm mb-6">
                  {t('serviceDetail.formDescription')}
                </p>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="service-contact-form">
                  <div>
                    <Label htmlFor="name">{t('form.name')} *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="form-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">{t('form.email')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="form-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">{t('form.phone')} *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="form-phone"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">{t('form.message')}</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      className="mt-1"
                      placeholder={language === 'en' ? 'Describe your project...' : 'Beschreiben Sie Ihr Projekt...'}
                      data-testid="form-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A] font-semibold"
                    data-testid="form-submit"
                  >
                    {isSubmitting ? t('contactPage.submitting') : t('form.submit')}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-[#E4E4E7]">
                  <p className="text-sm text-[#71717A] mb-2">{language === 'en' ? 'Or call us:' : 'Oder rufen Sie uns an:'}</p>
                  <a 
                    href="tel:017634681594" 
                    className="flex items-center gap-2 text-[#D4B982] font-semibold hover:text-[#C5A059] transition-colors"
                  >
                    <Phone size={18} />
                    0176 34681594
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      {relatedServices.length > 0 && (
        <section className="py-16 bg-[#F4F4F5]">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-[#0A0A0A] mb-8">{t('serviceDetail.otherServices')}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedServices.map((relService) => (
                <Link
                  key={relService.id}
                  to={`/leistungen/${relService.slug}`}
                  className="group bg-white p-6 border border-[#E4E4E7] hover:border-[#D4B982] transition-all card-hover"
                  data-testid={`related-service-${relService.slug}`}
                >
                  <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2 group-hover:text-[#D4B982] transition-colors">
                    {getLocalizedField(relService, 'title')}
                  </h3>
                  <p className="text-[#71717A] text-sm mb-4 line-clamp-2">
                    {getLocalizedField(relService, 'short')}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-[#D4B982]">
                    {t('services.learnMore')}
                    <ChevronRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
